import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NEON = 'linear-gradient(135deg,#f9004d,#00f0ff)';
const GOLD = 'linear-gradient(135deg,#f4d778,#b8860b)';
const FR = 'linear-gradient(135deg,#2a5db0,#eef2f7,#e0454a)';

function Medal({ b, t }) {
  const ring = b.unlocked ? (b.gate ? GOLD : NEON) : '#26262f';
  return (
    <div className="flex flex-col items-center gap-1.5 text-center">
      <div className="relative w-16 h-16 rounded-full grid place-items-center shrink-0" style={{ background: ring }}>
        <div
          className="w-[52px] h-[52px] rounded-full grid place-items-center text-2xl"
          style={{ background: '#181824', filter: b.unlocked ? 'none' : 'grayscale(1) brightness(0.7)' }}
        >
          <span aria-hidden="true">{b.e}</span>
        </div>
        {!b.unlocked && (
          <span className="absolute -right-1 -bottom-1 w-5 h-5 rounded-full bg-[#0c0c13] border border-white/15 grid place-items-center">
            <Lock className="h-2.5 w-2.5 text-zinc-500" />
          </span>
        )}
      </div>
      <div className={'text-[13px] font-bold leading-tight ' + (b.unlocked ? 'text-white' : 'text-zinc-500')}>{b.n}</div>
      <div className="text-[11px] text-zinc-500 leading-tight">{b.c}</div>
      {b.prog && !b.unlocked && (
        <div className="text-[10px] text-zinc-600 font-mono">{b.prog[0]} / {b.prog[1]}</div>
      )}
      {b.r && (
        <span className={'text-[10px] font-bold rounded-full px-2 py-0.5 ' + (b.big ? 'bg-emerald-400 text-emerald-950' : 'bg-[#e9c15a] text-[#1a1206]')}>
          🎁 {b.r}
        </span>
      )}
      {b.code && (
        <button
          onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(b.code); }}
          title={t('passeport.copyCode')}
          className="text-[10px] font-mono font-bold text-[#00f0ff] bg-[#00f0ff]/10 border border-[#00f0ff]/30 rounded px-2 py-0.5 hover:bg-[#00f0ff]/20"
        >
          {b.code} 📋
        </button>
      )}
    </div>
  );
}

export default function MonPasseport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${API_URL}/api/badges/me`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-20">
        <Loader2 className="h-10 w-10 text-[#f9004d] animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center pt-20 px-6 text-center">
        <p className="text-white text-lg font-bold mb-2">{t('passeport.loginToView')}</p>
        <button onClick={() => navigate('/login')} className="mt-2 rounded-full bg-[#f9004d] hover:bg-[#d80043] text-white font-bold px-6 py-3">{t('passeport.login')}</button>
      </div>
    );
  }

  const isHost = data.kind === 'host';
  const initial = (data.name || '?').charAt(0).toUpperCase();

  // Passeport verrouillé (voyageur sans réservation)
  if (data.gated) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 pb-16 px-4">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-20 h-20 rounded-full grid place-items-center mx-auto mb-6" style={{ background: GOLD }}>
            <Lock className="h-8 w-8 text-[#1a1206]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">{t('passeport.lockedTitle')}</h1>
          <p className="text-zinc-400 mb-8">
            {t('passeport.lockedDescPre')}
            {' '}<b className="text-white">{t('passeport.badgesN', { count: 104 })}</b> — {t('passeport.lockedDescPost')}
          </p>
          <button onClick={() => navigate('/activities')} className="rounded-full bg-[#f9004d] hover:bg-[#d80043] text-white font-bold px-7 py-3.5">
            {t('passeport.discover')}
          </button>
        </div>
      </div>
    );
  }

  const prizeTitle = isHost ? t('passeport.prizeHost') : t('passeport.prizeTraveler');
  const prizeDesc = isHost ? t('passeport.prizeHostDesc') : t('passeport.prizeTravelerDesc');
  const prizeGrad = isHost ? FR : GOLD;
  const prizeInk = isHost ? '#9db8ff' : '#e9c15a';

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
          {isHost ? t('passeport.passportHost') : t('passeport.passportTraveler')}
        </h1>

        {/* Hero */}
        <div className="grid md:grid-cols-[1fr_1.35fr] gap-4 mb-8">
          <div className="bg-zinc-900/50 border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full grid place-items-center text-xl font-bold text-white shrink-0" style={{ background: NEON }}>{initial}</div>
              <div>
                <div className="text-white text-lg font-bold">{data.name || t('passeport.you')}</div>
                <div className="text-[#00f0ff] text-sm font-semibold">{t('passeport.level', { n: data.level })}</div>
              </div>
            </div>
            <div className="mt-5 font-mono text-3xl font-bold text-white tabular-nums">
              {data.unlocked} <span className="text-zinc-500 text-base font-semibold">/ {data.total} {t('passeport.badgesWord')}</span>
            </div>
            <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden mt-3">
              <div className="h-full rounded-full" style={{ width: data.pct + '%', background: NEON }} />
            </div>
          </div>

          <div className="rounded-2xl p-6 relative overflow-hidden"
            style={{ background: 'linear-gradient(#13131c,#13131c) padding-box, ' + prizeGrad + ' border-box', border: '1px solid transparent' }}>
            <div className="font-bold text-lg mb-2" style={{ color: prizeInk }}>{prizeTitle}</div>
            <p className="text-zinc-300 text-sm mb-4 max-w-lg">{prizeDesc}</p>
            <div className="flex items-center gap-3">
              <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden flex-1">
                <div className="h-full rounded-full" style={{ width: data.pct + '%', background: prizeGrad }} />
              </div>
              <div className="font-mono font-bold text-sm" style={{ color: prizeInk }}>{data.pct} %</div>
            </div>
            <div className="text-zinc-500 text-xs mt-3">{t('passeport.prizeFootnote')}</div>
          </div>
        </div>

        {/* Catégories */}
        {data.cats.map((cat) => {
          const catU = cat.b.filter((x) => x.unlocked).length;
          return (
            <div key={cat.t} className="mb-8">
              <div className="flex items-baseline gap-3 mb-4 flex-wrap">
                <span className="font-mono text-xs font-bold text-black bg-zinc-400 rounded px-1.5 py-0.5">{cat.t}</span>
                <h2 className="text-lg font-bold text-white">{cat.name}</h2>
                {cat.sub && <span className="text-zinc-600 text-sm">· {cat.sub}</span>}
                <span className="ml-auto font-mono text-xs text-zinc-500 tabular-nums">{catU} / {cat.b.length}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {cat.b.map((b) => (
                  <div key={b.id} className="bg-zinc-900/50 border border-white/10 rounded-xl p-3">
                    <Medal b={b} t={t} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <p className="text-zinc-600 text-xs border-t border-white/10 pt-4 flex items-center gap-2">
          <MapPin className="h-3 w-3" /> {t('passeport.autoUnlock')}
        </p>
      </div>
    </div>
  );
}
